import { Grid, List, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Button } from "../../common/components/Button/Button";
import { Header } from "../../common/components/Header/Header";
import { QuestionnaireButton } from "../../common/components/QuestionnaireButton/QuestionnaireButton";
import { View } from "../../common/components/View/View";
import { routes } from "../../common/routes/routes";
import {
  QUESTIONNAIRE_IDS,
  QuestionnaireEntity,
} from "../../common/types/QuestionnaireEntity";
import {
  selectAllCompletionPercentages,
  selectExtraQuestionnaires,
  selectQuestionnaireById,
  setActiveQuestionnaire,
} from "../../features/questionnaire/questionnaireSlice";

export const Dashboard = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const completionPercentages = useAppSelector(selectAllCompletionPercentages);
  const dispatch = useAppDispatch();
  const useSelectQuestionnaireByLanguageId = (id: string) =>
    useAppSelector((state) => selectQuestionnaireById(state, id));

  const emotionalQuestionnaire = useSelectQuestionnaireByLanguageId(
    QUESTIONNAIRE_IDS.emotional
  ) as QuestionnaireEntity;
  const healthQuestionnaire = useSelectQuestionnaireByLanguageId(
    QUESTIONNAIRE_IDS.health
  ) as QuestionnaireEntity;
  const lifestyleQuestionnaire = useSelectQuestionnaireByLanguageId(
    QUESTIONNAIRE_IDS.lifestyle
  ) as QuestionnaireEntity;
  const engagementQuestionnaire = useSelectQuestionnaireByLanguageId(
    QUESTIONNAIRE_IDS.engagement
  ) as QuestionnaireEntity;
  const homeQuestionnaire = useSelectQuestionnaireByLanguageId(
    QUESTIONNAIRE_IDS.home
  ) as QuestionnaireEntity;

  const extraQuestionnaires = useAppSelector(selectExtraQuestionnaires);

  const ALLOW_ORIGIN: string = process.env.REACT_APP_MEMBER_PORTAL_ORIGIN ?? "";

  const handleQuestionnaireClick = (questionnaireID: string) => {
    dispatch(setActiveQuestionnaire(questionnaireID));
    navigate(`${routes.QUESTIONNAIRE}/${questionnaireID}`);
  };

  const handleSchedulingClick = () => {
    try {
      window.parent.postMessage({ type: "skip_hra" }, ALLOW_ORIGIN);
    } catch (error) {
      console.log(error);
    }
    navigate(routes.COMPLETED);
    window.location.reload();
  };

  return (
    <View
      className="dashboard"
      header={
        <Header
          sx={{
            padding: "1.75rem 1.25rem 10px",
            backgroundColor: "rgb(242, 241, 245)",
            zIndex: 1199,
            width: "100vw",
            position: "fixed",
            left: 0,
            top: 0,
          }}
        />
      }
    >
      <>
        <Grid item>
          <h1>{formatMessage({ id: "header" })}</h1>
          <p>{formatMessage({ id: "wellnessJourney" })}</p>
          <p>{formatMessage({ id: "assessmentReasons" })}</p>
        </Grid>
        <Grid
          sx={{
            width: "100%",
          }}
        >
          <List>
            {healthQuestionnaire && (
              <li>
                <QuestionnaireButton
                  questionnaire={healthQuestionnaire}
                  onQuestionnaireClick={handleQuestionnaireClick}
                  completionPercentage={
                    completionPercentages[healthQuestionnaire.id]
                  }
                />
              </li>
            )}
            {emotionalQuestionnaire && (
              <li>
                <QuestionnaireButton
                  questionnaire={emotionalQuestionnaire}
                  onQuestionnaireClick={handleQuestionnaireClick}
                  completionPercentage={
                    completionPercentages[emotionalQuestionnaire.id]
                  }
                />
              </li>
            )}
            {lifestyleQuestionnaire && (
              <li>
                <QuestionnaireButton
                  questionnaire={lifestyleQuestionnaire}
                  onQuestionnaireClick={handleQuestionnaireClick}
                  completionPercentage={
                    completionPercentages[lifestyleQuestionnaire.id]
                  }
                />
              </li>
            )}
            {engagementQuestionnaire && (
              <li>
                <QuestionnaireButton
                  questionnaire={engagementQuestionnaire}
                  onQuestionnaireClick={handleQuestionnaireClick}
                  completionPercentage={
                    completionPercentages[engagementQuestionnaire.id]
                  }
                />
              </li>
            )}
            {homeQuestionnaire && (
              <li key={QUESTIONNAIRE_IDS.home}>
                <QuestionnaireButton
                  questionnaire={homeQuestionnaire}
                  onQuestionnaireClick={handleQuestionnaireClick}
                  completionPercentage={
                    completionPercentages[homeQuestionnaire.id]
                  }
                />
              </li>
            )}
            {extraQuestionnaires.map((elem, index) => {
              return (
                <li>
                  <QuestionnaireButton
                    questionnaire={elem}
                    onQuestionnaireClick={handleQuestionnaireClick}
                    completionPercentage={completionPercentages[elem.id]}
                  />
                </li>
              );
            })}
          </List>
        </Grid>
        <Grid
          item
          sx={{
            marginTop: "auto",
            alignSelf: "center",
          }}
        >
          <Button
            type="text"
            onClick={handleSchedulingClick}
          >
            <Typography
              fontSize="1.15rem"
              fontWeight="700"
              component="span"
            >
              {formatMessage({ id: "continueWithScheduling" })}
            </Typography>
          </Button>
        </Grid>
      </>
    </View>
  );
};
